import { useEffect, useMemo, useState } from "react";
import useGuestListStore from "../../../../../../stores/useGuestListStore";
import { Box } from "@mui/system";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  Stack,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { CustomTextField } from "./styles";
import usePostGuestList from "../../../../../../Hooks/usePostGuestList";
import usePutGuestList from "../../../../../../Hooks/usePutGuestList";

const GuestListAdd = ({ chatId, onSuccess }) => {
  const {
    selectedGuest,

    mutatedGuestInfo,
    setMutatedGuestInfo,

    mutatedContactInfo,
    setMutatedContactInfo,

    loading,
    setLoading,

    setOpen,
    returnToView,
    resetMutatedGuest,
  } = useGuestListStore();

  const postGuestList = usePostGuestList();
  const putGuestList = usePutGuestList();

  const [showDuplicatePrimary, setShowDuplicatePrimary] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const isEditGuest = useMemo(() => !!selectedGuest, [selectedGuest]);

  const isEmailValid = useMemo(() => {
    const email = mutatedContactInfo.email.trim();
    // Simple email regex for validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email !== "" && emailRegex.test(email);
  }, [mutatedContactInfo.email]);

  // Validate required fields
  const isFormValid = useMemo(() => {
    // Check if all guests have firstName and lastName
    const allGuestsValid = mutatedGuestInfo.every(
      (guest) => guest.firstName.trim() !== "" && guest.lastName.trim() !== ""
    );

    return allGuestsValid && isEmailValid;
  }, [mutatedGuestInfo, isEmailValid]);

  // Handle guest info changes
  const handleGuestChange = (index, field, value) => {
    const updatedGuests = [...mutatedGuestInfo];
    updatedGuests[index][field] = value;
    setMutatedGuestInfo(updatedGuests);
  };

  // Add plus-one
  const handleAddPlusOne = () => {
    setMutatedGuestInfo([
      ...mutatedGuestInfo,
      { id: crypto.randomUUID(), firstName: "", lastName: "" },
    ]);
  };

  // Handle contact info changes
  const handleContactChange = (field, value) => {
    setMutatedContactInfo({ ...mutatedContactInfo, [field]: value });
  };

  const handleMutateData = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const [primaryGuest, ...additionalGuests] = mutatedGuestInfo;

      const sanitizedData = {
        eventId: String(chatId),
        primary: {
          firstName: primaryGuest.firstName,
          lastName: primaryGuest.lastName,
          email: mutatedContactInfo.email,
          // optional
          phone: mutatedContactInfo?.phone || undefined,
          relationship: mutatedContactInfo?.relationship || undefined,
          side: mutatedContactInfo?.side || undefined,
          seatOrTable: mutatedContactInfo?.seatOrTable || undefined,
        },
        additional:
          additionalGuests?.length > 0
            ? additionalGuests.map((guest) => ({
                firstName: guest.firstName,
                lastName: guest.lastName,
              }))
            : undefined,
      };

      if (isEditGuest) {
        putGuestList(
          { ...sanitizedData, id: selectedGuest.id },
          selectedGuest.id
        )
          .then(resolve)
          .catch(reject)
          .finally(() => {
            setLoading(false);
          });
      } else {
        postGuestList(sanitizedData)
          .then(resolve)
          .catch((err) => {
            if (err.response?.data?.message === "error.duplicateprimary") {
              setShowDuplicatePrimary(true);
            }
            reject(err);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  // Handle save & close
  const handleSaveAndClose = () => {
    handleMutateData()
      .then(() => {
        onSuccess();
        returnToView();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // Handle save & add another
  const handleSaveAndAddAnother = () => {
    handleMutateData()
      .then(() => {
        onSuccess();
        resetMutatedGuest();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (selectedGuest) {
      const { primary, additional } = selectedGuest;

      const guestInfo = [
        {
          id: primary.id,
          firstName: primary.firstName,
          lastName: primary.lastName,
        },
        ...additional
          .filter((guest) => !guest.isPrimary)
          .map((guest) => ({
            id: guest.id,
            firstName: guest.firstName,
            lastName: guest.lastName,
          })),
      ];
      const contactInfo = {
        email: primary.email,
        phone: primary.phone,
        relationship: primary.relationship,
        side: primary.side,
        seatOrTable: primary.seatOrTable,
      };
      setMutatedGuestInfo(guestInfo);
      setMutatedContactInfo(contactInfo);
    }
  }, [selectedGuest, setMutatedGuestInfo, setMutatedContactInfo]);

  return (
    <Box>
      {/* Header */}
      <Stack
        display="flex"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="20px"
      >
        <Stack display="flex" direction="row" alignItems="center" gap={1}>
          <ArrowBackIcon sx={{ cursor: "pointer" }} onClick={returnToView} />
          <Typography fontSize={20} fontWeight={700}>
            {isEditGuest ? "Edit Guest" : "Add Guest"}
          </Typography>
        </Stack>

        <CloseIcon sx={{ cursor: "pointer" }} onClick={handleClose} />
      </Stack>

      <Box overflow="auto" maxHeight="500px" margin="0 10px" padding="0 10px">
        {/* Guest Name Section */}
        <Box mb={3}>
          {mutatedGuestInfo.map((guest, index) => {
            const isPrimaryGuest = index === 0;

            return (
              <Box key={guest.id} mb={3}>
                <Typography fontSize={15} fontWeight={500} mb={1.5}>
                  Guest Name
                </Typography>

                <Stack spacing={1.5}>
                  <CustomTextField
                    fullWidth
                    placeholder="First Name (required)"
                    value={guest.firstName}
                    onChange={(e) => {
                      if (isPrimaryGuest) {
                        setShowDuplicatePrimary(false);
                      }
                      handleGuestChange(index, "firstName", e.target.value);
                    }}
                  />
                  <CustomTextField
                    fullWidth
                    placeholder="Last Name (required)"
                    value={guest.lastName}
                    onChange={(e) => {
                      if (isPrimaryGuest) {
                        setShowDuplicatePrimary(false);
                      }
                      handleGuestChange(index, "lastName", e.target.value);
                    }}
                  />
                </Stack>

                {showDuplicatePrimary && isPrimaryGuest && (
                  <Box display="flex" justifyContent="left">
                    <Typography fontSize={14} fontWeight={500} color="#fb4764">
                      Duplicated primary guest.
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}

          <Box display="flex" justifyContent="center">
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddPlusOne}
              disableRipple
              sx={{
                color: "#fb4764",
                fontWeight: 500,
                fontSize: 14,
                width: 140,
                height: 24,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Add Plus-One
            </Button>
          </Box>
        </Box>

        {/* Contact Information Section */}
        <Box mb={3}>
          <Typography fontSize={15} fontWeight={500} mb={1.5}>
            Contact Information
          </Typography>

          <Stack spacing={1.5}>
            <CustomTextField
              fullWidth
              placeholder="Email (required)"
              value={mutatedContactInfo.email}
              onChange={(e) => handleContactChange("email", e.target.value)}
              error={!isEmailValid && mutatedContactInfo.email.trim() !== ""}
              helperText={
                !isEmailValid &&
                mutatedContactInfo.email.trim() !== "" &&
                "Invalid email."
              }
              sx={{
                "& .MuiFormHelperText-root.Mui-error": {
                  color: "#fb4764",
                },
              }}
            />
            <CustomTextField
              fullWidth
              placeholder="Phone Number (Optional)"
              value={mutatedContactInfo.phone}
              onChange={(e) => handleContactChange("phone", e.target.value)}
            />
            <CustomTextField
              fullWidth
              placeholder="Relationship (Optional)"
              value={mutatedContactInfo.relationship}
              onChange={(e) =>
                handleContactChange("relationship", e.target.value)
              }
            />
          </Stack>
        </Box>

        {/* Side Section */}
        <Box mb={3}>
          <Typography fontSize={14} fontWeight={600} mb={1.5}>
            Side (Optional)
          </Typography>

          <RadioGroup
            row
            value={mutatedContactInfo.side}
            onChange={(e) => handleContactChange("side", e.target.value)}
          >
            <FormControlLabel
              value="BRIDE"
              control={
                <Radio
                  sx={{
                    color: "#fb4764",
                    size: 12,
                    padding: 0,
                    "&.Mui-checked": {
                      color: "#fb4764",
                    },
                  }}
                />
              }
              label={<Typography fontSize={14}>Bride</Typography>}
              sx={{
                boxSizing: "border-box",
                border: "1px solid #d9d9d9",
                borderRadius: "16px",
                padding: "12px 16px",
                width: "140px",
                marginLeft: 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            />
            <FormControlLabel
              value="GROOM"
              control={
                <Radio
                  sx={{
                    color: "#fb4764",
                    size: 12,
                    padding: 0,
                    "&.Mui-checked": {
                      color: "#fb4764",
                    },
                  }}
                />
              }
              label={<Typography fontSize={14}>Groom</Typography>}
              sx={{
                boxSizing: "border-box",
                border: "1px solid #d9d9d9",
                borderRadius: "16px",
                padding: "12px 16px",
                width: "140px",
                marginLeft: 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            />
            <FormControlLabel
              value="NONE"
              control={
                <Radio
                  sx={{
                    color: "#fb4764",
                    size: 12,
                    padding: 0,
                    "&.Mui-checked": {
                      color: "#fb4764",
                    },
                  }}
                />
              }
              label={<Typography fontSize={14}>None</Typography>}
              sx={{
                boxSizing: "border-box",
                border: "1px solid #d9d9d9",
                borderRadius: "16px",
                padding: "12px 16px",
                width: "140px",
                marginLeft: 0,
                marginRight: 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            />
          </RadioGroup>
        </Box>

        {/* Seat/Table Section */}
        <Box mb={2}>
          <CustomTextField
            fullWidth
            placeholder="Seat/Table (Optional)"
            value={mutatedContactInfo.seatOrTable}
            onChange={(e) => handleContactChange("seatOrTable", e.target.value)}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      <Stack
        direction="row"
        spacing={2}
        padding="20px"
        borderTop="1px solid #d9d9d9"
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={handleSaveAndClose}
          disabled={!isFormValid || loading}
          disableRipple
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: "10px",
            borderColor: "#d9d9d9",
            color: "#666",
            "&:hover": {
              borderColor: "#d9d9d9",
              backgroundColor: "transparent",
            },
            "&.Mui-disabled": {
              borderColor: "#E0E0E0",
              backgroundColor: "#E0E0E0",
              color: "#B0B0B0",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="secondary" />
          ) : (
            "Save & Close"
          )}
        </Button>

        <Button
          fullWidth
          variant="contained"
          onClick={handleSaveAndAddAnother}
          disabled={!isFormValid || loading}
          disableRipple
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: "10px",
            backgroundColor: "#fb4764",
            "&:hover": {
              backgroundColor: "#fb4764",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E0E0E0",
              color: "#B0B0B0",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="secondary" />
          ) : (
            "Save & Add Another"
          )}
        </Button>
      </Stack>
    </Box>
  );
};

export default GuestListAdd;
